import React, { useState, useRef, useCallback } from 'react';
import { Check, Circle, Clock, AlertCircle, Loader2, ExternalLink, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { isPast, isToday, isTomorrow, isYesterday, format } from 'date-fns';
import EditTaskModal from './EditTaskModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskMessage = ({ message, onTaskUpdated, onNavigateToChat }) => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [completing, setCompleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const cardRef = useRef(null);
  const bgRef = useRef(null);
  const iconRef = useRef(null);
  const touchStartRef = useRef(null);
  const currentXRef = useRef(0);
  const swipeThreshold = 40; // Low threshold for easy swiping
  
  const taskId = message.task_id;
  const isTaskMessage = taskId || message.content?.startsWith('☑️') || message.content?.startsWith('✅');
  
  if (!isTaskMessage) return null;

  const parseTaskContent = (content) => {
    const lines = content.split('\n');
    const title = lines[0]?.replace('☑️ ', '').replace('✅ ', '').replace(/\*\*/g, '') || 'Task';
    const dueMatch = content.match(/📅 Due: (.+)/);
    const priorityMatch = content.match(/🔴 Priority: (\w+)/);
    const assignedMatch = content.match(/👤 (Assigned to: .+|Self-assigned)/);
    return {
      title,
      dueDate: dueMatch ? dueMatch[1] : null,
      priority: priorityMatch ? priorityMatch[1] : 'medium',
      assigned: assignedMatch ? assignedMatch[1] : null,
      isCompleted: content.startsWith('✅')
    };
  };

  const taskInfo = parseTaskContent(message.content || '');
  
  const handleComplete = async () => {
    if (!taskId || taskInfo.isCompleted) return;
    setCompleting(true);
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { status: 'complete' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task completed!');
      onTaskUpdated && onTaskUpdated(taskId);
    } catch (error) {
      toast.error('Failed to complete task');
    } finally {
      setCompleting(false);
    }
  };

  // Direct DOM manipulation for buttery smooth swipe (no React re-renders)
  const onTouchStart = (e) => {
    if (taskInfo.isCompleted || !taskId) return;
    touchStartRef.current = e.touches[0].clientX;
    currentXRef.current = 0;
    // Remove transition during drag for instant response
    if (cardRef.current) cardRef.current.style.transition = 'none';
  };

  const onTouchMove = (e) => {
    if (touchStartRef.current === null || taskInfo.isCompleted) return;
    const deltaX = e.touches[0].clientX - touchStartRef.current;
    if (deltaX > 0) {
      const x = Math.min(deltaX, 120); // Direct 1:1 finger tracking
      currentXRef.current = x;
      const progress = Math.min(x / swipeThreshold, 1);
      
      // Direct DOM updates — no setState, no re-render
      if (cardRef.current) cardRef.current.style.transform = `translateX(${x}px)`;
      if (bgRef.current) bgRef.current.style.backgroundColor = progress > 0.8 ? '#22c55e' : `rgba(34,197,94,${progress * 0.6})`;
      if (iconRef.current) {
        iconRef.current.style.opacity = progress;
        iconRef.current.style.transform = `scale(${0.5 + progress * 0.5})`;
      }
    }
  };

  const onTouchEnd = () => {
    if (touchStartRef.current === null) return;
    const completed = currentXRef.current >= swipeThreshold;
    
    // Add spring-back transition
    if (cardRef.current) {
      cardRef.current.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      cardRef.current.style.transform = 'translateX(0)';
    }
    if (bgRef.current) {
      bgRef.current.style.transition = 'background-color 0.3s ease';
      bgRef.current.style.backgroundColor = 'transparent';
    }
    if (iconRef.current) {
      iconRef.current.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
      iconRef.current.style.opacity = '0';
      iconRef.current.style.transform = 'scale(0.5)';
    }
    
    if (completed) handleComplete();
    touchStartRef.current = null;
    currentXRef.current = 0;
  };

  const getDueDateStatus = () => {
    if (!taskInfo.dueDate || taskInfo.dueDate === 'No due date') return null;
    try {
      const date = new Date(taskInfo.dueDate);
      if (isNaN(date.getTime())) return null;
      if (isPast(date) && !isToday(date)) return { status: 'overdue', label: 'Overdue', color: 'text-red-500' };
      if (isToday(date)) return { status: 'today', label: 'Due today', color: 'text-yellow-500' };
      if (isTomorrow(date)) return { status: 'tomorrow', label: 'Due tomorrow', color: 'text-blue-500' };
      return { status: 'upcoming', label: taskInfo.dueDate, color: isDarkMode ? 'text-gray-400' : 'text-text-tertiary' };
    } catch {
      return null;
    }
  };

  const dueDateStatus = getDueDateStatus();
  const priorityColors = { high: 'bg-red-500', medium: 'bg-yellow-500', low: 'bg-green-500' };

  const getCreatedLabel = () => {
    if (!message.timestamp) return null;
    try {
      const date = new Date(message.timestamp);
      if (isNaN(date.getTime())) return null;
      const time = format(date, 'h:mm a');
      if (isToday(date)) return time;
      if (isYesterday(date)) return `Yesterday ${time}`;
      return format(date, 'dd MMM yyyy, h:mm a');
    } catch { return null; }
  };

  const createdLabel = getCreatedLabel();

  return (
    <>
    <div className="relative overflow-hidden rounded-2xl" data-testid={`task-card-${taskId}`}>
      {/* Swipe reveal background */}
      {!taskInfo.isCompleted && (
        <div 
          ref={bgRef}
          className="absolute inset-0 flex items-center pl-6 rounded-2xl"
          style={{ backgroundColor: 'transparent' }}
        >
          <Check 
            ref={iconRef}
            size={28} 
            className="text-white"
            style={{ opacity: 0, transform: 'scale(0.5)', willChange: 'transform, opacity' }} 
          />
        </div>
      )}
      
      {/* Task card */}
      <div 
        ref={cardRef}
        className={`rounded-2xl p-4 ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        } border ${
          taskInfo.isCompleted 
            ? isDarkMode ? 'border-green-800' : 'border-green-200'
            : dueDateStatus?.status === 'overdue'
              ? isDarkMode ? 'border-red-800' : 'border-red-200'
              : isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}
        style={{ willChange: 'transform', touchAction: 'pan-y' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex items-start gap-3">
          <button
            onClick={handleComplete}
            disabled={completing || taskInfo.isCompleted || !taskId}
            className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
              taskInfo.isCompleted
                ? 'bg-green-500 border-green-500 text-white'
                : completing
                  ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                  : 'border-gray-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10'
            }`}
            data-testid={`complete-task-${taskId}`}
          >
            {completing ? (
              <Loader2 size={14} className="animate-spin text-[#D4AF37]" />
            ) : taskInfo.isCompleted ? (
              <Check size={14} />
            ) : null}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <h4 className={`font-medium ${
                taskInfo.isCompleted 
                  ? 'line-through text-gray-400' 
                  : isDarkMode ? 'text-white' : 'text-text-primary'
              }`}>
                {taskInfo.title}
              </h4>
              {message.source_chat_id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigateToChat) {
                      onNavigateToChat(message.source_chat_id, message.source_message_id);
                    } else {
                      navigate(`/chat/${message.source_chat_id}`);
                    }
                  }}
                  className={`flex-shrink-0 ml-2 p-1 rounded-full transition-colors ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}
                  title="Go to source message"
                  data-testid={`task-goto-source-${taskId}`}
                >
                  <ExternalLink size={14} />
                </button>
              )}
              {!taskInfo.isCompleted && taskId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEditModal(true);
                  }}
                  className={`flex-shrink-0 ml-1 p-1 rounded-full transition-colors ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}
                  title="Edit task"
                  data-testid={`edit-task-${taskId}`}
                >
                  <Pencil size={14} />
                </button>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className={`px-2 py-0.5 rounded-full text-xs text-white capitalize ${priorityColors[taskInfo.priority] || priorityColors.medium}`}>
                {taskInfo.priority}
              </span>
              
              {dueDateStatus && (
                <span className={`flex items-center gap-1 text-xs ${dueDateStatus.color}`}>
                  {dueDateStatus.status === 'overdue' ? <AlertCircle size={12} /> : <Clock size={12} />}
                  {dueDateStatus.label}
                </span>
              )}
              
              {taskInfo.assigned && (
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-text-tertiary'}`}>
                  {taskInfo.assigned}
                </span>
              )}

              {createdLabel && (
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {createdLabel}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    {showEditModal && (
      <EditTaskModal
        task={{
          taskId,
          title: taskInfo.title,
          description: '',
          priority: taskInfo.priority,
          assignedTo: message.assigned_to || '',
          dueDate: taskInfo.dueDate && taskInfo.dueDate !== 'No due date' ? (() => {
            try {
              const d = new Date(taskInfo.dueDate);
              return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
            } catch { return ''; }
          })() : '',
        }}
        chatId={message.source_chat_id || message.chat_id}
        onClose={() => setShowEditModal(false)}
        onSuccess={() => onTaskUpdated && onTaskUpdated(taskId)}
      />
    )}
    </>
  );
};

export default TaskMessage;
